import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { ArrowLeft, Loader2, Download, ImageIcon, Film, RefreshCw } from 'lucide-react'
import { api } from '@/lib/api'
import { toast } from '@/components/ui/toaster'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type {
  FeedbackVariant,
  FeedbackReferenceGroup,
  ReviewUpdate,
  CandidateAttribution,
  MarketAttribution,
} from '@/types'

const SECTION_STYLES: Record<string, { label: string; border: string; bg: string; badge: string }> = {
  A: { label: 'A · 变体迭代', border: 'border-l-blue-600', bg: 'bg-blue-50', badge: 'text-blue-600' },
  B: { label: 'B · 新方向', border: 'border-l-violet-600', bg: 'bg-violet-50', badge: 'text-violet-600' },
  C: { label: 'C · 竞品借鉴', border: 'border-l-orange-600', bg: 'bg-orange-50', badge: 'text-orange-600' },
}

const DECISIONS: { value: string; label: string; active: string }[] = [
  { value: 'adopt', label: '采纳', active: 'bg-green-600 text-white border-green-600' },
  { value: 'reject', label: '拒绝', active: 'bg-red-600 text-white border-red-600' },
]

export function FeedbackPage() {
  // 从总览页带入的预选参数（/finance/feedback/review?date=...&market=...&type=...）
  const [searchParams, setSearchParams] = useSearchParams()
  const [creativeType, setCreativeType] = useState(searchParams.get('type') || 'image')
  const [date, setDate] = useState<string>(searchParams.get('date') || '')
  const [market, setMarket] = useState<string>(searchParams.get('market') || '')

  const { data: batches, isLoading: batchesLoading } = useQuery({
    queryKey: ['feedback-batches'],
    queryFn: () => api.getFeedbackBatches(),
  })

  // available types / dates / markets derived from batches
  const types = useMemo(
    () => Array.from(new Set((batches ?? []).map((b) => b.creative_type).filter(Boolean))) as string[],
    [batches],
  )
  const dates = useMemo(
    () =>
      Array.from(
        new Set(
          (batches ?? [])
            .filter((b) => b.creative_type === creativeType)
            .map((b) => b.date)
            .filter(Boolean),
        ),
      ) as string[],
    [batches, creativeType],
  )
  const markets = useMemo(
    () =>
      Array.from(
        new Set(
          (batches ?? [])
            .filter((b) => b.creative_type === creativeType && b.date === date)
            .map((b) => b.market)
            .filter(Boolean),
        ),
      ) as string[],
    [batches, creativeType, date],
  )

  // URL 指向的批次不存在时提示（auto-select 会兜底换到可用批次，但不能无声）
  const initialParamsRef = useRef({
    date: searchParams.get('date'),
    market: searchParams.get('market'),
  })
  const deadLinkWarnedRef = useRef(false)
  useEffect(() => {
    if (deadLinkWarnedRef.current || !batches?.length) return
    const wanted = initialParamsRef.current
    if (!wanted.date || !wanted.market) {
      deadLinkWarnedRef.current = true
      return
    }
    deadLinkWarnedRef.current = true
    const exists = batches.some((b) => b.date === wanted.date && b.market === wanted.market)
    if (!exists) {
      toast({
        title: `链接指向的批次（${wanted.date} / ${wanted.market}）不存在，已切换到可用批次`,
        variant: 'destructive',
      })
    }
  }, [batches])

  // auto-select first available type/date/market when batches or filters change
  useEffect(() => {
    if (types.length && !types.includes(creativeType)) setCreativeType(types[0])
  }, [types, creativeType])
  useEffect(() => {
    if (dates.length && !dates.includes(date)) setDate(dates[0])
  }, [dates, date])
  useEffect(() => {
    if (markets.length && !markets.includes(market)) setMarket(markets[0])
  }, [markets, market])

  // 选择变化时回写 URL（replace），保证刷新/分享/前进后退都指向当前批次
  useEffect(() => {
    if (!date || !market) return
    const next = { type: creativeType, date, market }
    const current = {
      type: searchParams.get('type') ?? '',
      date: searchParams.get('date') ?? '',
      market: searchParams.get('market') ?? '',
    }
    if (current.type !== next.type || current.date !== next.date || current.market !== next.market) {
      setSearchParams(next, { replace: true })
    }
  }, [creativeType, date, market, searchParams, setSearchParams])

  const enabled = Boolean(date && market)
  const {
    data: batchAssets,
    isLoading: assetsLoading,
    isFetching: assetsFetching,
    refetch: refetchAssets,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['feedback-assets', date, market, creativeType],
    queryFn: () => api.getFeedbackAssets({ date, market, type: creativeType }),
    enabled,
    // Live sync so reviewers see each other's updates:
    refetchInterval: 20000, // poll every 20s (only while tab is visible)
    refetchOnWindowFocus: true,
    staleTime: 0,
    placeholderData: keepPreviousData, // keep showing current data during refetch (no blanking)
  })

  // download stats snapshot (server values at load time)
  const downloadStats = useMemo(() => {
    let total = 0
    let downloaded = 0
    for (const sec of batchAssets?.sections ?? []) {
      for (const g of sec.groups) {
        for (const v of g.variants) {
          total += 1
          if (v.download_count > 0) downloaded += 1
        }
      }
    }
    return { total, downloaded }
  }, [batchAssets])

  const handleExport = async () => {
    try {
      const blob = await api.exportFeedback({ date, market, type: creativeType })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `feedback_${market}_${date}.jsonl`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast({ title: '导出失败', variant: 'destructive' })
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-[1180px] mx-auto">
      <div>
        <Link
          to="/finance/feedback"
          className="mb-1 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          返回总览
        </Link>
        <h1 className="text-2xl font-bold">金融素材反馈</h1>
        <p className="text-sm text-muted-foreground">按类型 / 日期 / 市场查看迭代素材并填写反馈。</p>
      </div>

      {/* Selectors */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">类型</div>
          <div className="flex gap-1">
            {['image', 'video'].map((t) => {
              const available = types.includes(t)
              return (
                <button
                  key={t}
                  disabled={!available}
                  onClick={() => setCreativeType(t)}
                  className={cn(
                    'flex items-center gap-1 px-3 py-1.5 rounded-md text-sm border transition-colors',
                    creativeType === t
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                    !available && 'opacity-40 cursor-not-allowed',
                  )}
                >
                  {t === 'image' ? <ImageIcon className="h-4 w-4" /> : <Film className="h-4 w-4" />}
                  {t === 'image' ? '图片' : '视频'}
                </button>
              )
            })}
          </div>
        </div>

        <Selector label="日期" value={date} options={dates} onChange={setDate} />
        <Selector label="市场" value={market} options={markets} onChange={setMarket} />

        <div className="ml-auto flex items-center gap-3">
          {enabled && batchAssets && (
            <span className="text-xs text-muted-foreground">
              已下载 {downloadStats.downloaded}/{downloadStats.total} 张
            </span>
          )}
          {enabled && dataUpdatedAt > 0 && (
            <span className="text-xs text-muted-foreground">
              更新于 {new Date(dataUpdatedAt).toLocaleTimeString()}
            </span>
          )}
          <Button
            variant="outline"
            size="icon"
            onClick={() => refetchAssets()}
            disabled={!enabled || assetsFetching}
            title="刷新（每 20 秒自动刷新）"
          >
            <RefreshCw className={cn('h-4 w-4', assetsFetching && 'animate-spin')} />
          </Button>
          <Button variant="outline" onClick={handleExport} disabled={!enabled}>
            <Download className="h-4 w-4 mr-1" /> 导出 jsonl
          </Button>
        </div>
      </div>

      {/* Content */}
      {batchesLoading || assetsLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !batches?.length ? (
        <EmptyState text="还没有导入任何反馈批次。请先用 import_finance_feedback 脚本导入。" />
      ) : !batchAssets?.sections.length ? (
        <EmptyState text="该批次下没有素材。" />
      ) : (
        <div className="space-y-8">
          <MarketAttributionPanel data={batchAssets.market_attribution} />
          {batchAssets.sections.map((section) => {
            const style = SECTION_STYLES[section.variant_type] ?? {
              label: section.variant_type,
              border: 'border-l-gray-400',
              bg: 'bg-gray-50',
              badge: 'text-gray-600',
            }
            return (
              <div key={section.variant_type} className="space-y-4">
                <div className={cn('flex items-center gap-2 px-4 py-2.5 rounded-md border-l-4', style.border, style.bg)}>
                  <span className={cn('font-bold', style.badge)}>{style.label}</span>
                </div>
                {section.groups.map((group, gi) => (
                  <ReferenceGroup key={group.reference_asset_id ?? gi} group={group} />
                ))}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Selector({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: string[]
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-muted-foreground">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!options.length}
        className="h-9 min-w-[160px] rounded-md border border-gray-200 bg-white px-3 text-sm disabled:opacity-50"
      >
        {!options.length && <option value="">—</option>}
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  )
}

function EmptyState({ text }: { text: string }) {
  return <div className="text-center py-20 text-sm text-muted-foreground">{text}</div>
}

function MarketAttributionPanel({ data }: { data: MarketAttribution | null }) {
  if (!data) return null
  const hasAny =
    data.summary ||
    data.winning_recipe ||
    data.traffic_trap_warning ||
    data.commonalities.length ||
    data.reference_value.length
  if (!hasAny) return null
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-4 space-y-2 text-xs">
      <div className="font-bold text-sm">📊 总体归因报告</div>
      {data.summary && <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{data.summary}</p>}
      {data.winning_recipe && (
        <p className="text-gray-700">
          <span className="font-semibold text-green-700">🏆 制胜配方：</span>
          {data.winning_recipe}
        </p>
      )}
      {data.commonalities.length > 0 && (
        <div>
          <div className="font-semibold">✅ 共性</div>
          <ul className="list-disc pl-5 text-gray-700 space-y-0.5">
            {data.commonalities.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
      {data.traffic_trap_warning && (
        <p className="text-gray-700">
          <span className="font-semibold text-red-600">⚠️ 流量陷阱：</span>
          {data.traffic_trap_warning}
        </p>
      )}
      {data.reference_value.length > 0 && (
        <div>
          <div className="font-semibold">🎯 下一轮动作</div>
          <ul className="list-disc pl-5 text-gray-700 space-y-0.5">
            {data.reference_value.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
    </div>
  )
}

function AttributionBlock({ data }: { data: CandidateAttribution }) {
  const m = data.metrics
  const stat = (label: string, val: number | null, suffix = '') =>
    val == null ? null : (
      <div className="flex justify-between gap-2">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{val}{suffix}</span>
      </div>
    )
  return (
    <div className="rounded-md border border-gray-200 bg-white p-2 space-y-1 text-[11px]">
      <div className="font-semibold text-gray-700">📈 归因报告</div>
      <div className="space-y-0.5">
        {stat('消耗', m.spend)}
        {stat('消耗占比', m.spend_pct, '%')}
        {stat('CTR', m.ctr, '%')}
        {stat('授信成本', m.credit_cost)}
        {stat('放款成本', m.loan_cost)}
      </div>
      <details className="mt-1">
        <summary className="cursor-pointer text-blue-600 select-none">归因分析</summary>
        <div className="mt-1 space-y-1.5 text-gray-700 leading-snug">
          {data.visual_summary && <p>🖼 画面：{data.visual_summary}</p>}
          {data.attribution && <p>🎯 归因：{data.attribution}</p>}
          {data.visual_levers.length > 0 && <p>🎨 视觉杠杆：{data.visual_levers.join('、')}</p>}
          {data.color_analysis.length > 0 && (
            <div>🌈 配色分析：
              <ul className="list-disc pl-4">{data.color_analysis.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </div>
          )}
          {data.composition.length > 0 && (
            <div>📐 画面构成：
              <ul className="list-disc pl-4">{data.composition.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </div>
          )}
          {data.success_factors.length > 0 && (
            <div>🏆 效果好的原因：
              <ul className="list-disc pl-4">{data.success_factors.map((c, i) => <li key={i}>{c}</li>)}</ul>
            </div>
          )}
        </div>
      </details>
    </div>
  )
}

function formatReviewedAt(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString()
}

function ReferenceGroup({ group }: { group: FeedbackReferenceGroup }) {
  const hasInsight = Boolean(group.core_insight) || group.evidence.length > 0
  const queryClient = useQueryClient()

  // One reviewer shared by every variant in this row. Seed from any variant that
  // already has a recorded reviewer so re-opening keeps the previous value.
  const [reviewedBy, setReviewedBy] = useState(
    () => group.variants.find((v) => v.review.reviewed_by)?.review.reviewed_by ?? '',
  )
  // Per-variant review draft (feedback + decision stay per-image), keyed by id.
  const [reviews, setReviews] = useState<Record<string, { feedbackText: string; decision: string | null }>>(
    () =>
      Object.fromEntries(
        group.variants.map((v) => [
          v.id,
          { feedbackText: v.review.feedback_text ?? '', decision: v.review.decision ?? null },
        ]),
      ),
  )

  const updateReview = (id: string, patch: Partial<{ feedbackText: string; decision: string | null }>) =>
    setReviews((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))

  // Only variants the reviewer actually touched get saved.
  const dirtyVariants = group.variants.filter((v) => {
    const r = reviews[v.id]
    return Boolean(r?.feedbackText.trim()) || r?.decision != null
  })
  const hasContent = dirtyVariants.length > 0
  const needsReviewer = hasContent && !reviewedBy.trim()

  // Latest existing review across the row, for the "上次" hint.
  const lastReview = useMemo(() => {
    let best: { reviewed_by: string; reviewed_at: string } | null = null
    for (const v of group.variants) {
      const rb = v.review.reviewed_by
      if (!rb) continue
      const at = v.review.reviewed_at ?? ''
      if (!best || at > best.reviewed_at) best = { reviewed_by: rb, reviewed_at: at }
    }
    return best
  }, [group.variants])

  const mutation = useMutation({
    mutationFn: async () => {
      const by = reviewedBy.trim()
      await Promise.all(
        dirtyVariants.map((v) => {
          const r = reviews[v.id]
          const body: ReviewUpdate = {
            feedback_text: r.feedbackText,
            decision: r.decision,
            score: null,
            reviewed_by: by,
          }
          return api.saveFeedbackReview(v.id, body)
        }),
      )
    },
    onSuccess: () => {
      toast({ title: '已保存反馈' })
      queryClient.invalidateQueries({ queryKey: ['feedback-assets'] })
    },
    onError: () => toast({ title: '保存失败', variant: 'destructive' }),
  })

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex gap-5">
        {/* source side: reference image (A/C) and/or market insight (B/C) */}
        <div className="w-56 shrink-0 space-y-3">
          {group.reference_url && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">
                {group.reference_kind === 'foreplay_competitor' ? '竞品参考' : '参考原素材'}
              </div>
              <img src={group.reference_url} alt="reference" className="w-full rounded-md border object-contain" />
              {group.reference_text && <div className="mt-1 text-xs font-medium">{group.reference_text}</div>}
              {group.reference_memo && (
                <div className="mt-1 text-[11px] text-muted-foreground">{group.reference_memo}</div>
              )}
            </div>
          )}

          {group.attribution && <AttributionBlock data={group.attribution} />}

          {hasInsight && <InsightCard group={group} />}

          {!group.reference_url && !hasInsight && !group.attribution && (
            <div className="w-full aspect-square rounded-md border bg-gray-50 flex items-center justify-center text-xs text-muted-foreground">
              无参考素材
            </div>
          )}
        </div>
        <div className="text-2xl text-gray-300 self-center">→</div>
        {/* variants + one shared reviewer / save for the whole row */}
        <div className="flex-1 space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {group.variants.map((v) => (
              <VariantCard
                key={v.id}
                variant={v}
                feedbackText={reviews[v.id]?.feedbackText ?? ''}
                decision={reviews[v.id]?.decision ?? null}
                onFeedbackChange={(t) => updateReview(v.id, { feedbackText: t })}
                onDecisionChange={(d) => updateReview(v.id, { decision: d })}
              />
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-3">
            {lastReview && (
              <div className="mr-auto text-[10px] text-muted-foreground">
                上次：{lastReview.reviewed_by}
                {lastReview.reviewed_at && ` · ${formatReviewedAt(lastReview.reviewed_at)}`}
              </div>
            )}
            <Input
              value={reviewedBy}
              onChange={(e) => setReviewedBy(e.target.value)}
              placeholder={hasContent ? '反馈人（必填，本组共用）' : '反馈人（本组共用）'}
              className={cn('h-8 w-48 text-xs', needsReviewer && 'border-red-300')}
            />
            {needsReviewer && <span className="text-[10px] text-red-500">填写反馈人后才能保存</span>}
            <Button
              size="sm"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !hasContent || needsReviewer}
            >
              {mutation.isPending ? '保存中…' : '保存本组'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function InsightCard({ group }: { group: FeedbackReferenceGroup }) {
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3">
      <div className="text-xs text-muted-foreground mb-1">💡 市场洞察</div>
      {group.core_insight && (
        <div className="text-[12px] leading-relaxed text-gray-700">{group.core_insight}</div>
      )}
      {group.evidence.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            📎 引用源（{group.evidence.length}）
          </div>
          <ul className="space-y-2">
            {group.evidence.map((ev, i) => (
              <li key={i} className="flex gap-2.5">
                {ev.image_url ? (
                  <img
                    src={ev.image_url}
                    alt={ev.filename ?? 'evidence'}
                    className="w-24 h-24 rounded border object-contain bg-white shrink-0"
                  />
                ) : (
                  <div className="w-24 h-24 rounded border bg-gray-100 shrink-0 flex items-center justify-center text-[10px] text-muted-foreground">
                    无图
                  </div>
                )}
                <div className="text-[11px] leading-snug">
                  {ev.filename && <span className="font-semibold">{ev.filename}</span>}
                  {ev.filename && '：'}
                  <span className="text-muted-foreground">{ev.text}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function VariantCard({
  variant,
  feedbackText,
  decision,
  onFeedbackChange,
  onDecisionChange,
}: {
  variant: FeedbackVariant
  feedbackText: string
  decision: string | null
  onFeedbackChange: (value: string) => void
  onDecisionChange: (value: string | null) => void
}) {
  const [downloading, setDownloading] = useState(false)
  // Freeze the preview URL: polling re-signs presigned URLs (prod) which would
  // otherwise reload the image on every refresh. The image content never changes.
  const [imgUrl, setImgUrl] = useState(variant.generated_url)

  useEffect(() => {
    if (!imgUrl && variant.generated_url) setImgUrl(variant.generated_url)
  }, [variant.generated_url, imgUrl])

  const direction = variant.direction as Record<string, string>
  const verifier = variant.verifier as Record<string, unknown>

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const res = await api.recordFeedbackDownload(variant.id)
      if (res.download_url) {
        const a = document.createElement('a')
        a.href = res.download_url
        a.download = ''
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
    } catch {
      toast({ title: '下载失败', variant: 'destructive' })
    } finally {
      setDownloading(false)
    }
  }

  const decisionVal = verifier?.decision as string | undefined
  const scoreVal = verifier?.score as number | undefined

  return (
    <div className="rounded-md border border-gray-200 overflow-hidden flex flex-col">
      {imgUrl ? (
        <img src={imgUrl} alt="variant" className="w-full object-contain bg-gray-50" />
      ) : (
        <div className="w-full aspect-square bg-gray-50" />
      )}
      <div className="flex items-center justify-end px-3 py-2 border-b border-gray-100 bg-gray-50/50">
        <Button
          size="sm"
          onClick={handleDownload}
          disabled={downloading || !variant.generated_url}
          className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Download className="h-3.5 w-3.5 mr-1" />
          {downloading ? '下载中…' : '下载原图'}
        </Button>
      </div>
      <div className="p-3 flex flex-col flex-1">
        {/* text block grows so the review form below is pinned to the card bottom,
            keeping the decision/feedback rows aligned across cards of differing text length */}
        <div className="space-y-2 flex-1">
          {direction?.title && <div className="text-sm font-semibold">{direction.title}</div>}
          {direction?.visual_plan && (
            <div className="text-[11px] text-muted-foreground whitespace-pre-wrap break-words">{direction.visual_plan}</div>
          )}
          {direction?.copy_plan && (
            <div className="text-[11px] text-gray-600 whitespace-pre-wrap break-words">📝 {direction.copy_plan}</div>
          )}
          {decisionVal && (
            <div className="text-[11px]">
              <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                机器评定: {decisionVal}
                {typeof scoreVal === 'number' ? ` · ${scoreVal}` : ''}
              </span>
            </div>
          )}
        </div>

        {/* per-image decision + feedback — reviewer and save live at the row level */}
        <div className="pt-2 mt-2 border-t border-gray-100 space-y-2">
          <div className="flex gap-2">
            {DECISIONS.map((d) => (
              <button
                key={d.value}
                onClick={() => onDecisionChange(decision === d.value ? null : d.value)}
                className={cn(
                  'px-3 py-1 rounded text-xs border transition-colors',
                  decision === d.value ? d.active : 'border-gray-200 text-gray-600 hover:bg-gray-50',
                )}
              >
                {d.label}
              </button>
            ))}
          </div>
          <Textarea
            value={feedbackText}
            onChange={(e) => onFeedbackChange(e.target.value)}
            placeholder="填写反馈…"
            className="min-h-[60px] text-sm"
          />
        </div>
      </div>
    </div>
  )
}
